import { useEffect, useRef } from "react";
import Plyr from "plyr";
import Hls from "hls.js";
import "plyr/dist/plyr.css";

const WORKER_URL =
  import.meta.env.VITE_VIDEO_WORKER_URL ??
  "https://course-videos.ciao-54a.workers.dev";

type Props = {
  guid: string;
  videoId?: string;
  /** Marketing videos served without a token (must be in the worker's PUBLIC_GUIDS) */
  isPublic?: boolean;
  autoplay?: boolean;
  onProgress?: (videoId: string, currentTime: number, duration: number) => void;
};

/**
 * Drop-in replacement for the Bunny iframe player.
 * Same engine Bunny uses (Plyr + hls.js), configured identically:
 * same controls, same speed menu, same quality menu with Auto,
 * same seek-preview thumbnails, same accent color.
 */
export default function R2Player({ guid, videoId, isPublic = false, autoplay = false, onProgress }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const plyrRef = useRef<Plyr | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const saveTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let disposed = false;

    const savedKey = videoId ? `video_position_${videoId}` : null;

    // iOS Safari resets the page scroll when a video LEAVES fullscreen. Because
    // the player usually sits far down the course page, the user lands somewhere
    // random and has to scroll a long way back to find the video. Remember where
    // we were on the way into fullscreen and pull the player back into view on the
    // way out. These are the native iOS video events, so they fire regardless of
    // how fullscreen was triggered (Plyr button or the OS controls).
    let preFsScrollY = 0;
    const onEnterFs = () => {
      preFsScrollY = window.scrollY;
      document.documentElement.style.overflow = "hidden";
    };
    const onExitFs = () => {
      document.documentElement.style.overflow = "";
      const restore = () => {
        window.scrollTo(0, preFsScrollY);
        videoRef.current?.scrollIntoView({ block: "center" });
      };
      // Safari performs its own scroll reset right after this event; run after it.
      requestAnimationFrame(restore);
      setTimeout(restore, 150);
    };

    const savePosition = (currentTime: number, duration: number) => {
      if (!savedKey || !currentTime || !duration) return;
      const isNearEnd = currentTime > duration - 30 || currentTime / duration > 0.95;
      if (isNearEnd) {
        localStorage.removeItem(savedKey);
      } else {
        localStorage.setItem(savedKey, currentTime.toString());
        if (videoId) onProgress?.(videoId, currentTime, duration);
      }
    };

    async function setup() {
      let query = "";
      if (!isPublic) {
        const res = await fetch(`${WORKER_URL}/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ guid }),
        });
        if (!res.ok) {
          console.error("[R2Player] token request failed", res.status);
          return;
        }
        const { token, exp } = await res.json();
        query = `?token=${token}&exp=${exp}`;
      }
      if (disposed || !video) return;

      const src = `${WORKER_URL}/${guid}/playlist.m3u8${query}`;

      const plyrOptions: Plyr.Options = {
        controls: [
          "play-large", "rewind", "play", "fast-forward", "progress",
          "current-time", "mute", "volume", "captions", "settings",
          "pip", "airplay", "fullscreen",
        ],
        settings: ["quality", "speed"],
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4] },
        ratio: "16:9",
        // On iPhone, use iOS's real native fullscreen player: it rotates to
        // landscape and fills the whole screen (with its own Done button). The CSS
        // fallback can't do that — a 16:9 video letterboxed into a portrait screen
        // just looks small in a big black box, which is the exact complaint. The
        // CSS fallback (with our overrides below) stays on for non-iOS browsers.
        fullscreen: { enabled: true, fallback: true, iosNative: true },
        autoplay,
        previewThumbnails: {
          enabled: true,
          src: `${WORKER_URL}/${guid}/thumbnails.vtt`,
        },
      };

      if (Hls.isSupported()) {
        const hls = new Hls({
          xhrSetup: (xhr, url) => {
            // every playlist/segment request carries the auth token
            if (query && !url.includes("token=")) {
              xhr.open("GET", url + query, true);
            }
          },
        });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (disposed) return;
          const heights = hls.levels.map(l => l.height).sort((a, b) => b - a);
          plyrRef.current = new Plyr(video, {
            ...plyrOptions,
            quality: {
              default: 0, // 0 = Auto
              options: [0, ...heights],
              forced: true,
              onChange: (q: number) => {
                hls.currentLevel = q === 0 ? -1 : hls.levels.findIndex(l => l.height === q);
              },
            },
            i18n: { qualityLabel: { 0: "Auto" } },
          });
          wireEvents();
        });
      } else {
        // Safari: native HLS
        video.src = src;
        plyrRef.current = new Plyr(video, plyrOptions);
        wireEvents();
      }
    }

    function wireEvents() {
      const saved = savedKey ? parseFloat(localStorage.getItem(savedKey) || "0") : 0;
      if (saved > 0) {
        const seekOnce = () => {
          if (video!.currentTime < saved) video!.currentTime = saved;
          video!.removeEventListener("loadedmetadata", seekOnce);
        };
        video!.addEventListener("loadedmetadata", seekOnce);
        if (video!.readyState >= 1) seekOnce();
      }
      video!.addEventListener("timeupdate", () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = window.setTimeout(
          () => savePosition(video!.currentTime, video!.duration),
          2000
        );
      });
      video!.addEventListener("ended", () => {
        if (savedKey) localStorage.removeItem(savedKey);
      });

      // Native iOS fullscreen (webkitEnterFullscreen). Covers the Plyr button and
      // the OS-provided fullscreen control alike.
      video!.addEventListener("webkitbeginfullscreen", onEnterFs);
      video!.addEventListener("webkitendfullscreen", onExitFs);
      // Non-iOS fallback / real Fullscreen API path.
      plyrRef.current?.on("enterfullscreen", onEnterFs);
      plyrRef.current?.on("exitfullscreen", onExitFs);
    }

    setup();

    return () => {
      disposed = true;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      plyrRef.current?.destroy();
      plyrRef.current = null;
    };
  }, [guid, videoId, isPublic, autoplay]);

  return (
    <div
      className="w-full h-full [&_.plyr]:h-full [&_.plyr]:w-full [&_.plyr]:min-h-full"
      style={{ "--plyr-color-main": "#8B54FD" } as React.CSSProperties}
    >
      <video ref={videoRef} className="w-full h-full" playsInline controls={false} />
    </div>
  );
}
